import React, { useEffect, useState } from "react";
import { api, fmtDate, fmtINR, formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, MapPin, Camera, ShieldCheck, IndianRupee, User } from "lucide-react";
import StatusBadge from "@/components/crm/StatusBadge";
import { SearchBox, DeleteButton, matchesQuery } from "@/components/crm/RecordActions";
import FilterBar, { FilterSelect, inDateRange } from "@/components/crm/Filters";
import { PhotoField, PhotoGallery, FileField } from "@/components/crm/PhotoUpload";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const STATUSES = ["Site Survey","Material Dispatched","Structure Erected","Panels Mounted","Net Metering","Subsidy Disbursement Pending","Subsidy Received","Commissioned"];
const SUBSIDY_STATUS = ["Pending","Applied","Approved","Received"];
const empty = {
  customer_id:"", site_address:"", system_capacity:"", material:"", installation_team:"",
  status:"Site Survey", installation_date:"", commissioning_date:"", notes:"",
  site_photos:[],
  panel_serial_no:"", panel_serial_photo:null,
  inverter_serial_no:"", inverter_serial_photo:null,
  gps_lat:null, gps_lng:null, gps_photo:null,
  // KYC
  customer_photo:null, aadhar_photo:null, aadhar_no:"",
  pan_photo:null, pan_no:"", passbook_photo:null, account_no:"", ifsc:"",
  // Site tech photos
  acdb_photo:null, dcdb_photo:null, inverter_photo:null, solar_plant_photo:null,
  net_meter_photo:null, earthing_photo:null,
  // Subsidy
  subsidy_amount:null, subsidy_status:"Pending", subsidy_reference:"", subsidy_received_date:"",
};

export default function Projects() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [search, setSearch] = useState("");
  const [fFrom, setFFrom] = useState("");
  const [fTo, setFTo] = useState("");
  const [fStatus, setFStatus] = useState("__all__");

  const load = async () => {
    setItems((await api.get("/projects")).data);
    setCustomers((await api.get("/customers")).data);
  };
  useEffect(()=>{ load(); },[]);

  const save = async () => {
    try {
      const payload = {...form};
      if (payload.subsidy_amount === "" ) payload.subsidy_amount = null;
      if (editId) await api.patch(`/projects/${editId}`, payload);
      else await api.post("/projects", payload);
      toast.success("Saved");
      setOpen(false); setForm(empty); setEditId(null); load();
    } catch(e){ toast.error(formatError(e.response?.data?.detail)); }
  };

  const edit = (p) => { setForm({...empty, ...p}); setEditId(p.id); setOpen(true); };

  const del = async (p) => {
    try { await api.delete(`/projects/${p.id}`); toast.success(`Project ${p.project_no} deleted`); load(); }
    catch(e){ toast.error(formatError(e.response?.data?.detail)); }
  };

  const captureGps = () => {
    if (!navigator.geolocation) { toast.error("GPS not available"); return; }
    navigator.geolocation.getCurrentPosition(
      (p) => { setForm(f => ({...f, gps_lat:p.coords.latitude, gps_lng:p.coords.longitude})); toast.success(`GPS captured (${p.coords.latitude.toFixed(4)}, ${p.coords.longitude.toFixed(4)})`); },
      () => toast.error("Unable to capture GPS"),
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const visible = items
    .filter((p) => matchesQuery(p, search))
    .filter((p) => inDateRange(p.created_at, fFrom, fTo))
    .filter((p) => fStatus === "__all__" || p.status === fStatus);

  return (
    <div className="space-y-5" data-testid="projects-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-3xl font-extrabold text-slate-900" style={{fontFamily:'Outfit'}}>Projects <span className="text-slate-400 text-lg font-medium">/ प्रोजेक्ट</span></h1>
          <p className="text-slate-500 text-sm">Site survey to commissioning — photos, GPS, KYC docs, subsidy tracking all in one place.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <SearchBox value={search} onChange={setSearch} placeholder="Search customer, project #..." testid="projects-search-input" />
          <Dialog open={open} onOpenChange={(v)=>{setOpen(v); if(!v){setForm(empty); setEditId(null);}}}>
            <DialogTrigger asChild>
              <Button className="bg-amber-500 hover:bg-amber-600 text-white" data-testid="add-project-btn"><Plus className="w-4 h-4 mr-1"/>New Project</Button>
            </DialogTrigger>
            <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
              <DialogHeader><DialogTitle>{editId?"Edit":"New"} Project</DialogTitle></DialogHeader>

              <div className="text-xs font-bold uppercase tracking-widest text-slate-500 border-b pb-1">Project Details</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="sm:col-span-2">
                  <Label className="text-xs font-semibold text-slate-600">Customer*</Label>
                  <Select value={form.customer_id} onValueChange={v=>setForm({...form, customer_id:v})}>
                    <SelectTrigger data-testid="project-customer-select"><SelectValue placeholder="Select customer"/></SelectTrigger>
                    <SelectContent>{customers.map(c=><SelectItem key={c.id} value={c.id}>{c.name} — {c.mobile}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="sm:col-span-2">
                  <Label className="text-xs font-semibold text-slate-600">Site Address</Label>
                  <Textarea rows={2} value={form.site_address} onChange={e=>setForm({...form, site_address:e.target.value})}/>
                </div>
                <div><Label className="text-xs font-semibold text-slate-600">System Capacity</Label><Input value={form.system_capacity} onChange={e=>setForm({...form, system_capacity:e.target.value})} placeholder="e.g. 5 kW"/></div>
                <div><Label className="text-xs font-semibold text-slate-600">Installation Team</Label><Input value={form.installation_team} onChange={e=>setForm({...form, installation_team:e.target.value})}/></div>
                <div><Label className="text-xs font-semibold text-slate-600">Project Status</Label>
                  <Select value={form.status} onValueChange={v=>setForm({...form, status:v})}>
                    <SelectTrigger data-testid="project-status-select"><SelectValue/></SelectTrigger>
                    <SelectContent>{STATUSES.map(s=><SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label className="text-xs font-semibold text-slate-600">Installation Date</Label><Input type="date" value={form.installation_date||""} onChange={e=>setForm({...form, installation_date:e.target.value})}/></div>
                <div><Label className="text-xs font-semibold text-slate-600">Commissioning Date</Label><Input type="date" value={form.commissioning_date||""} onChange={e=>setForm({...form, commissioning_date:e.target.value})}/></div>
                <div className="sm:col-span-2"><Label className="text-xs font-semibold text-slate-600">Material Details</Label><Textarea rows={2} value={form.material} onChange={e=>setForm({...form, material:e.target.value})}/></div>
                <div className="sm:col-span-2"><Label className="text-xs font-semibold text-slate-600">Notes</Label><Textarea rows={2} value={form.notes} onChange={e=>setForm({...form, notes:e.target.value})}/></div>
              </div>

              <div className="mt-3 text-xs font-bold uppercase tracking-widest text-slate-500 border-b pb-1 flex items-center gap-2"><User className="w-3.5 h-3.5"/>Customer KYC Documents</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-2">
                <PhotoField label="Customer Photo" value={form.customer_photo} onChange={(p)=>setForm({...form, customer_photo:p})} testid="project-customer-photo" />
                <div>
                  <Label className="text-xs font-semibold text-slate-600">Aadhar Number</Label>
                  <Input value={form.aadhar_no||""} maxLength={12} placeholder="12-digit" onChange={e=>setForm({...form, aadhar_no:e.target.value})} data-testid="project-aadhar-no"/>
                  <div className="mt-2"><FileField label="Aadhar Card (PDF or Image)" value={form.aadhar_photo} onChange={(p)=>setForm({...form, aadhar_photo:p})} testid="project-aadhar-photo" /></div>
                </div>
                <div>
                  <Label className="text-xs font-semibold text-slate-600">PAN Number</Label>
                  <Input value={form.pan_no||""} maxLength={10} placeholder="ABCDE1234F" onChange={e=>setForm({...form, pan_no:e.target.value.toUpperCase()})} data-testid="project-pan-no"/>
                  <div className="mt-2"><FileField label="PAN Card (PDF or Image)" value={form.pan_photo} onChange={(p)=>setForm({...form, pan_photo:p})} testid="project-pan-photo" /></div>
                </div>
                <div>
                  <div className="grid grid-cols-2 gap-2">
                    <div><Label className="text-xs font-semibold text-slate-600">Bank A/c No.</Label><Input value={form.account_no||""} onChange={e=>setForm({...form, account_no:e.target.value})} data-testid="project-account-no"/></div>
                    <div><Label className="text-xs font-semibold text-slate-600">IFSC</Label><Input value={form.ifsc||""} onChange={e=>setForm({...form, ifsc:e.target.value.toUpperCase()})} data-testid="project-ifsc"/></div>
                  </div>
                  <div className="mt-2"><FileField label="Bank Passbook (PDF or Image)" value={form.passbook_photo} onChange={(p)=>setForm({...form, passbook_photo:p})} testid="project-passbook-photo" /></div>
                </div>
              </div>

              <div className="mt-3 text-xs font-bold uppercase tracking-widest text-slate-500 border-b pb-1">Electricity Bill & Co-Applicant KYC (PDF or Image)</div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-2">
                <FileField label="Electricity Bill" value={form.electricity_bill} onChange={(p)=>setForm({...form, electricity_bill:p})} testid="project-electricity-bill" />
                <div className="space-y-2">
                  <div><Label className="text-xs font-semibold text-slate-600">Co-Applicant Name</Label>
                    <Input value={form.co_applicant_name||""} onChange={e=>setForm({...form, co_applicant_name:e.target.value})} data-testid="project-co-name"/></div>
                  <div><Label className="text-xs font-semibold text-slate-600">Relation</Label>
                    <Input value={form.co_applicant_relation||""} onChange={e=>setForm({...form, co_applicant_relation:e.target.value})} placeholder="Father / Spouse" data-testid="project-co-relation"/></div>
                </div>
                <div className="space-y-2">
                  <div><Label className="text-xs font-semibold text-slate-600">Co-App. Aadhaar No.</Label>
                    <Input value={form.co_applicant_aadhar_no||""} onChange={e=>setForm({...form, co_applicant_aadhar_no:e.target.value})} data-testid="project-co-aadhar-no"/></div>
                  <FileField label="Co-App. Aadhaar Card" value={form.co_applicant_aadhar_photo} onChange={(p)=>setForm({...form, co_applicant_aadhar_photo:p})} testid="project-co-aadhar-photo"/>
                </div>
                <div className="space-y-2 sm:col-span-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div><Label className="text-xs font-semibold text-slate-600">Co-App. PAN No.</Label>
                    <Input value={form.co_applicant_pan_no||""} onChange={e=>setForm({...form, co_applicant_pan_no:e.target.value.toUpperCase()})} data-testid="project-co-pan-no"/></div>
                  <FileField label="Co-App. PAN Card" value={form.co_applicant_pan_photo} onChange={(p)=>setForm({...form, co_applicant_pan_photo:p})} testid="project-co-pan-photo"/>
                </div>
              </div>

              <div className="mt-3 text-xs font-bold uppercase tracking-widest text-slate-500 border-b pb-1 flex items-center gap-2"><Camera className="w-3.5 h-3.5"/>Site Photos & GPS</div>
              <div className="mt-2"><PhotoGallery label="Site Visit Photos" value={form.site_photos||[]} onChange={(arr)=>setForm({...form, site_photos:arr})} testid="project-site-photos" max={12}/></div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 items-end mt-2">
                <div><Label className="text-xs font-semibold text-slate-600">Latitude</Label><Input type="number" step="0.000001" value={form.gps_lat??""} onChange={e=>setForm({...form, gps_lat:parseFloat(e.target.value)||null})} data-testid="project-gps-lat"/></div>
                <div><Label className="text-xs font-semibold text-slate-600">Longitude</Label><Input type="number" step="0.000001" value={form.gps_lng??""} onChange={e=>setForm({...form, gps_lng:parseFloat(e.target.value)||null})} data-testid="project-gps-lng"/></div>
                <Button type="button" variant="outline" onClick={captureGps} data-testid="project-capture-gps"><MapPin className="w-4 h-4 mr-1 text-emerald-600"/>Capture GPS</Button>
              </div>
              <div className="mt-2"><PhotoField label="GPS-tagged Site Photo" value={form.gps_photo} onChange={(p,g)=>setForm({...form, gps_photo:p, ...(g?{gps_lat:g.lat, gps_lng:g.lng}:{})})} testid="project-gps-photo" withGps /></div>

              <div className="mt-3 text-xs font-bold uppercase tracking-widest text-slate-500 border-b pb-1">Equipment Serial Numbers & Proof Photos</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-2">
                <div>
                  <Label className="text-xs font-semibold text-slate-600">Panel Serial No.</Label>
                  <Input value={form.panel_serial_no||""} onChange={e=>setForm({...form, panel_serial_no:e.target.value})} placeholder="e.g. WSMD-585-12345" data-testid="project-panel-serial"/>
                  <div className="mt-2"><PhotoField label="Panel Serial Photo" value={form.panel_serial_photo} onChange={(p)=>setForm({...form, panel_serial_photo:p})} testid="project-panel-serial-photo" /></div>
                </div>
                <div>
                  <Label className="text-xs font-semibold text-slate-600">Inverter Serial No.</Label>
                  <Input value={form.inverter_serial_no||""} onChange={e=>setForm({...form, inverter_serial_no:e.target.value})} placeholder="e.g. SG15KTL-M-6789" data-testid="project-inverter-serial"/>
                  <div className="mt-2"><PhotoField label="Inverter Serial Photo" value={form.inverter_serial_photo} onChange={(p)=>setForm({...form, inverter_serial_photo:p})} testid="project-inverter-serial-photo" /></div>
                </div>
              </div>

              <div className="mt-3 text-xs font-bold uppercase tracking-widest text-slate-500 border-b pb-1">Installed Equipment Photos</div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mt-2">
                <PhotoField label="ACDB Photo" value={form.acdb_photo} onChange={(p)=>setForm({...form, acdb_photo:p})} testid="project-acdb-photo" />
                <PhotoField label="DCDB Photo" value={form.dcdb_photo} onChange={(p)=>setForm({...form, dcdb_photo:p})} testid="project-dcdb-photo" />
                <PhotoField label="Inverter Photo" value={form.inverter_photo} onChange={(p)=>setForm({...form, inverter_photo:p})} testid="project-inverter-photo" />
                <PhotoField label="Solar Plant Photo" value={form.solar_plant_photo} onChange={(p)=>setForm({...form, solar_plant_photo:p})} testid="project-solar-plant-photo" />
                <PhotoField label="Net Meter Photo" value={form.net_meter_photo} onChange={(p)=>setForm({...form, net_meter_photo:p})} testid="project-net-meter-photo" />
                <PhotoField label="Earthing Photo" value={form.earthing_photo} onChange={(p)=>setForm({...form, earthing_photo:p})} testid="project-earthing-photo" />
              </div>

              <div className="mt-3 text-xs font-bold uppercase tracking-widest text-slate-500 border-b pb-1 flex items-center gap-2"><IndianRupee className="w-3.5 h-3.5"/>Subsidy Tracking</div>
              <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 mt-2">
                <div><Label className="text-xs font-semibold text-slate-600">Subsidy Amount (₹)</Label><Input type="number" value={form.subsidy_amount??""} onChange={e=>setForm({...form, subsidy_amount:parseFloat(e.target.value)||null})} data-testid="project-subsidy-amount"/></div>
                <div><Label className="text-xs font-semibold text-slate-600">Subsidy Status</Label>
                  <Select value={form.subsidy_status||"Pending"} onValueChange={v=>setForm({...form, subsidy_status:v})}>
                    <SelectTrigger data-testid="project-subsidy-status"><SelectValue/></SelectTrigger>
                    <SelectContent>{SUBSIDY_STATUS.map(s=><SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label className="text-xs font-semibold text-slate-600">Reference No.</Label><Input value={form.subsidy_reference||""} placeholder="Sanction / PMSY reg." onChange={e=>setForm({...form, subsidy_reference:e.target.value})} data-testid="project-subsidy-ref"/></div>
                <div><Label className="text-xs font-semibold text-slate-600">Received Date</Label><Input type="date" value={form.subsidy_received_date||""} onChange={e=>setForm({...form, subsidy_received_date:e.target.value})} data-testid="project-subsidy-date"/></div>
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={()=>setOpen(false)}>Cancel</Button>
                <Button className="bg-amber-500 hover:bg-amber-600" onClick={save} disabled={!form.customer_id} data-testid="save-project-btn">Save</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <FilterBar from={fFrom} to={fTo} onFrom={setFFrom} onTo={setFTo}
        onClear={()=>{setFFrom("");setFTo("");setFStatus("__all__");setSearch("");}}>
        <FilterSelect value={fStatus} onChange={setFStatus} placeholder="Status" testid="filter-status"
          options={STATUSES.map(s=>({value:s,label:s}))} />
      </FilterBar>

      <Card><CardContent className="p-0">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Project #</TableHead><TableHead>Customer</TableHead>
            <TableHead>Capacity</TableHead><TableHead>Subsidy</TableHead>
            <TableHead>Status</TableHead><TableHead>Install / Comm.</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {visible.length===0 && <TableRow><TableCell colSpan={7} className="text-center py-10 text-slate-400">{search ? `No projects matching "${search}"` : "No projects yet."}</TableCell></TableRow>}
            {visible.map(p=>(
              <TableRow key={p.id} data-testid={`project-row-${p.project_no}`}>
                <TableCell className="font-mono text-xs">{p.project_no}</TableCell>
                <TableCell className="font-medium">{p.customer_name}<div className="text-xs text-slate-500">{p.customer_mobile}</div></TableCell>
                <TableCell className="font-medium text-amber-700">{p.system_capacity}</TableCell>
                <TableCell>
                  {p.subsidy_amount ? (
                    <div className="text-xs">
                      <div className="font-mono">{fmtINR(p.subsidy_amount)}</div>
                      <StatusBadge value={p.subsidy_status||"Pending"}/>
                    </div>
                  ) : <span className="text-slate-400 text-xs">-</span>}
                </TableCell>
                <TableCell><StatusBadge value={p.status}/></TableCell>
                <TableCell className="text-xs">{fmtDate(p.installation_date)}<div>{fmtDate(p.commissioning_date)}</div></TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-1">
                    <Button size="sm" variant="ghost" onClick={()=>edit(p)} data-testid={`edit-project-${p.project_no}`}>Edit / View</Button>
                    {user?.role === "admin" && (
                      <DeleteButton title={`Delete Project ${p.project_no}?`} description={`This will permanently delete the project for ${p.customer_name}. This action cannot be undone.`} onConfirm={()=>del(p)} testid={`delete-project-${p.project_no}`} />
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent></Card>
    </div>
  );
}
